import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiShield, FiDollarSign, FiTruck, FiEye, FiFileText, FiSettings, FiActivity, FiTrendingUp, FiUser, FiMapPin, FiClock, FiCheckCircle, FiXCircle, FiAlertTriangle, FiLock, FiEdit } from 'react-icons/fi';
import { useTokenIssuer, ISSUER_PERMISSIONS } from '../../contexts/TokenIssuerContext';
import { useTokenization } from '../../contexts/TokenizationContext';
import IssuerMintingInterface from './IssuerMintingInterface';
import IssuerVerificationCenter from './IssuerVerificationCenter';
import IssuerRedemptionCenter from './IssuerRedemptionCenter';
import IssuerDeliveryCenter from './IssuerDeliveryCenter';

// Styled Components
const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #1a1a3e 0%, #2d2d5f 100%);
  color: #fff;
  padding: 2rem;
`;

const HeroSection = styled.section`
  text-align: center;
  margin-bottom: 3rem;
  padding: 2rem 0;

  h1 {
    font-size: 3rem;
    font-weight: 800;
    background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    margin-bottom: 1rem;
  }

  p {
    font-size: 1.2rem;
    color: #a0a0a0;
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.6;
  }
`;

const IssuerInfo = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;

  .issuer-avatar {
    width: 60px;
    height: 60px;
    background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    color: #fff;
  }

  .issuer-details {
    flex: 1;

    .issuer-name {
      font-size: 1.5rem;
      font-weight: 600;
      color: #fff;
      margin-bottom: 0.25rem;
    }

    .issuer-type {
      color: #4facfe;
      margin-bottom: 0.5rem;
    }

    .issuer-stats {
      display: flex;
      gap: 2rem;
      font-size: 0.9rem;

      .stat {
        display: flex;
        align-items: center;
        gap: 0.25rem;
        color: #a0a0a0;

        .value {
          color: #fff;
          font-weight: 600;
        }
      }
    }
  }

  .issuer-status {
    text-align: center;

    .status-badge {
      padding: 0.5rem 1rem;
      background: rgba(34, 197, 94, 0.2);
      color: #22c55e;
      border: 1px solid rgba(34, 197, 94, 0.3);
      border-radius: 20px;
      font-size: 0.85rem;
      font-weight: 600;
      text-transform: uppercase;
      margin-bottom: 0.5rem;
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
    }

    .permissions-count {
      color: #a0a0a0;
      font-size: 0.85rem;
    }
  }
`;

const CapabilitiesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 3rem;
`;

const CapabilityCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-5px);
    border-color: #4facfe;
    box-shadow: 0 10px 30px rgba(79, 172, 254, 0.15);
  }

  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;

    &:hover {
      transform: none;
      border-color: rgba(255, 255, 255, 0.1);
      box-shadow: none;
    }
  }

  .capability-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1rem;
  }

  .capability-icon {
    width: 50px;
    height: 50px;
    background: ${props => props.enabled ? 
      'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' : 
      'rgba(107, 114, 128, 0.3)'
    };
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    color: #fff;
  }

  .capability-info {
    flex: 1;

    .capability-name {
      font-size: 1.2rem;
      font-weight: 600;
      color: #fff;
      margin-bottom: 0.25rem;
    }

    .capability-description {
      color: #a0a0a0;
      font-size: 0.9rem;
      line-height: 1.4;
    }
  }

  .capability-status {
    position: absolute;
    top: 1rem;
    right: 1rem;
    font-size: 1rem;
    color: ${props => props.enabled ? '#22c55e' : '#ef4444'};
  }

  .capability-stats {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    display: flex;
    justify-content: space-between;
    font-size: 0.85rem;

    .stat {
      text-align: center;

      .stat-value {
        font-weight: 600;
        color: #fff;
        display: block;
      }

      .stat-label {
        color: #a0a0a0;
      }
    }
  }
`;

const QuickActions = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 3rem;
`;

const ActionButton = styled.button`
  padding: 1rem;
  background: ${props => props.primary ? 
    'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' :
    'rgba(255, 255, 255, 0.05)'
  };
  border: 1px solid ${props => props.primary ? 'transparent' : 'rgba(255, 255, 255, 0.2)'};
  border-radius: 8px;
  color: #fff;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(79, 172, 254, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  .action-icon {
    font-size: 1.2rem;
  }

  .action-text {
    font-weight: 600;
  }
`;

const StatsSection = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 2rem;

  .section-title {
    font-size: 1.3rem;
    font-weight: 600;
    color: #fff;
    margin-bottom: 1.5rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    .icon {
      color: #4facfe;
    }
  }

  .stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 1.5rem;
  }

  .stat-card {
    text-align: center;

    .stat-value {
      font-size: 2rem;
      font-weight: 700;
      color: #fff;
      margin-bottom: 0.25rem;
    }

    .stat-label {
      color: #a0a0a0;
      font-size: 0.9rem;
    }

    .stat-change {
      font-size: 0.8rem;
      margin-top: 0.25rem;
      
      &.positive {
        color: #22c55e;
      }
      
      &.negative {
        color: #ef4444;
      }
    }
  }
`;

const RecentActivity = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 2rem;

  .section-title {
    font-size: 1.3rem;
    font-weight: 600;
    color: #fff;
    margin-bottom: 1.5rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    .icon {
      color: #4facfe;
    }
  }

  .activity-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .activity-item {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem;
    background: rgba(255, 255, 255, 0.02);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;

    .activity-icon {
      width: 40px;
      height: 40px;
      background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
    }

    .activity-details {
      flex: 1;

      .activity-title {
        color: #fff;
        font-weight: 500;
        margin-bottom: 0.25rem;
      }

      .activity-description {
        color: #a0a0a0;
        font-size: 0.85rem;
      }
    }

    .activity-time {
      color: #a0a0a0;
      font-size: 0.8rem;
    }
  }

  .empty-activity {
    text-align: center;
    padding: 2rem;
    color: #a0a0a0;

    .empty-icon {
      font-size: 3rem;
      margin-bottom: 1rem;
      color: #4facfe;
    }
  }
`;

// Mock current issuer data - this would come from authentication context
const currentIssuer = {
  id: 'issuer-1',
  name: 'Premium Metals Custody LLC',
  type: 'specialized_custodian',
  email: 'operations@premiummetals.com',
  status: 'active',
  permissions: [
    ISSUER_PERMISSIONS.MINT_TOKENS,
    ISSUER_PERMISSIONS.BURN_TOKENS,
    ISSUER_PERMISSIONS.VERIFY_ASSETS,
    ISSUER_PERMISSIONS.PHYSICAL_DELIVERY,
    ISSUER_PERMISSIONS.WAREHOUSE_OPERATIONS,
    ISSUER_PERMISSIONS.ASSET_NULLIFICATION,
    ISSUER_PERMISSIONS.REDEMPTION_PROCESSING
  ],
  allowedCategories: ['preciousMetals', 'collectibles'],
  allowedRegions: ['north_america', 'europe'],
  stats: {
    totalIssuances: 1247,
    totalBurned: 89,
    pendingVerifications: 12,
    activeDeliveries: 5,
    totalVolume: 5650000
  }
};

const IssuerDashboard = () => {
  const { hasPermission } = useTokenIssuer();
  const [activeInterface, setActiveInterface] = useState(null);

  // Define capabilities based on permissions
  const capabilities = [
    {
      id: 'minting',
      name: 'Token Minting',
      description: 'Mint new tokens for verified assets',
      icon: <FiDollarSign />,
      permission: ISSUER_PERMISSIONS.MINT_TOKENS,
      stats: { total: currentIssuer.stats.totalIssuances, pending: 3, success: 98.5 },
      component: 'IssuerMintingInterface'
    },
    {
      id: 'verification',
      name: 'Asset Verification',
      description: 'Verify physical assets and documentation',
      icon: <FiShield />,
      permission: ISSUER_PERMISSIONS.VERIFY_ASSETS,
      stats: { total: currentIssuer.stats.pendingVerifications, pending: 5, success: 96.2 },
      component: 'IssuerVerificationCenter'
    },
    {
      id: 'delivery',
      name: 'Physical Delivery',
      description: 'Manage asset delivery and logistics',
      icon: <FiTruck />,
      permission: ISSUER_PERMISSIONS.PHYSICAL_DELIVERY,
      stats: { total: currentIssuer.stats.activeDeliveries, pending: 2, success: 94.8 },
      component: 'IssuerDeliveryCenter'
    },
    {
      id: 'redemption',
      name: 'Token Redemption',
      description: 'Process redemption requests and asset releases',
      icon: <FiDollarSign />,
      permission: ISSUER_PERMISSIONS.REDEMPTION_PROCESSING,
      stats: { total: currentIssuer.stats.totalBurned, pending: 1, success: 99.1 },
      component: 'IssuerRedemptionCenter'
    },
    {
      id: 'warehouse',
      name: 'Warehouse Operations',
      description: 'Manage warehouse inventory and operations',
      icon: <FiMapPin />,
      permission: ISSUER_PERMISSIONS.WAREHOUSE_OPERATIONS,
      stats: { total: 156, pending: 0, success: 100 },
      component: null
    },
    {
      id: 'nullification',
      name: 'Asset Nullification',
      description: 'Handle asset expiration and nullification',
      icon: <FiXCircle />,
      permission: ISSUER_PERMISSIONS.ASSET_NULLIFICATION,
      stats: { total: 23, pending: 0, success: 100 },
      component: null
    }
  ];

  const enabledCapabilities = capabilities.filter(cap => 
    hasPermission(currentIssuer.id, cap.permission)
  );

  const handleCapabilityClick = (capability) => {
    if (capability.component && hasPermission(currentIssuer.id, capability.permission)) {
      setActiveInterface(capability.component);
    }
  };

  const closeInterface = () => {
    setActiveInterface(null);
  };

  const renderInterface = () => {
    switch (activeInterface) {
      case 'IssuerMintingInterface':
        return <IssuerMintingInterface onClose={closeInterface} />;
      case 'IssuerVerificationCenter':
        return <IssuerVerificationCenter onClose={closeInterface} />;
      case 'IssuerRedemptionCenter':
        return <IssuerRedemptionCenter onClose={closeInterface} />;
      case 'IssuerDeliveryCenter':
        return <IssuerDeliveryCenter onClose={closeInterface} />;
      default:
        return null;
    }
  };

  return (
    <Container>
      <HeroSection>
        <h1>Issuer Operations Center</h1>
        <p>Manage your authorized token operations and asset custody services</p>
      </HeroSection>

      <IssuerInfo>
        <div className="issuer-avatar">
          {currentIssuer.name.charAt(0)}
        </div>
        <div className="issuer-details">
          <div className="issuer-name">{currentIssuer.name}</div>
          <div className="issuer-type">Specialized Custodian</div>
          <div className="issuer-stats">
            <div className="stat">
              Categories: <span className="value">{currentIssuer.allowedCategories.length}</span>
            </div>
            <div className="stat">
              Regions: <span className="value">{currentIssuer.allowedRegions.length}</span>
            </div>
            <div className="stat">
              Permissions: <span className="value">{currentIssuer.permissions.length}</span>
            </div>
          </div>
        </div>
        <div className="issuer-status">
          <div className="status-badge">
            <FiCheckCircle />
            {currentIssuer.status}
          </div>
          <div className="permissions-count">
            {enabledCapabilities.length} Active Capabilities
          </div>
        </div>
      </IssuerInfo>

      <QuickActions>
        <ActionButton 
          primary
          onClick={() => setActiveInterface('IssuerMintingInterface')}
          disabled={!hasPermission(currentIssuer.id, ISSUER_PERMISSIONS.MINT_TOKENS)}
        >
          <FiDollarSign className="action-icon" />
          <span className="action-text">Mint Tokens</span>
        </ActionButton>
        <ActionButton 
          onClick={() => setActiveInterface('IssuerVerificationCenter')}
          disabled={!hasPermission(currentIssuer.id, ISSUER_PERMISSIONS.VERIFY_ASSETS)}
        >
          <FiShield className="action-icon" />
          <span className="action-text">Verify Assets</span>
        </ActionButton>
        <ActionButton 
          onClick={() => setActiveInterface('IssuerDeliveryCenter')}
          disabled={!hasPermission(currentIssuer.id, ISSUER_PERMISSIONS.PHYSICAL_DELIVERY)}
        >
          <FiTruck className="action-icon" />
          <span className="action-text">Manage Delivery</span>
        </ActionButton>
        <ActionButton 
          onClick={() => setActiveInterface('IssuerRedemptionCenter')}
          disabled={!hasPermission(currentIssuer.id, ISSUER_PERMISSIONS.REDEMPTION_PROCESSING)}
        >
          <FiDollarSign className="action-icon" />
          <span className="action-text">Process Redemption</span>
        </ActionButton>
      </QuickActions>

      <CapabilitiesGrid>
        {capabilities.map(capability => (
          <CapabilityCard
            key={capability.id}
            enabled={hasPermission(currentIssuer.id, capability.permission)}
            className={!hasPermission(currentIssuer.id, capability.permission) ? 'disabled' : ''}
            onClick={() => handleCapabilityClick(capability)}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ y: -5 }}
          >
            <div className="capability-status">
              {hasPermission(currentIssuer.id, capability.permission) ? 
                <FiCheckCircle /> : <FiLock />
              }
            </div>
            
            <div className="capability-header">
              <div className="capability-icon">
                {capability.icon}
              </div>
              <div className="capability-info">
                <div className="capability-name">{capability.name}</div>
                <div className="capability-description">{capability.description}</div>
              </div>
            </div>

            {hasPermission(currentIssuer.id, capability.permission) && (
              <div className="capability-stats">
                <div className="stat">
                  <span className="stat-value">{capability.stats.total}</span>
                  <span className="stat-label">Total</span>
                </div>
                <div className="stat">
                  <span className="stat-value">{capability.stats.pending}</span>
                  <span className="stat-label">Pending</span>
                </div>
                <div className="stat">
                  <span className="stat-value">{capability.stats.success}%</span>
                  <span className="stat-label">Success</span>
                </div>
              </div>
            )}
          </CapabilityCard>
        ))}
      </CapabilitiesGrid>

      <StatsSection>
        <div className="section-title">
          <FiTrendingUp className="icon" />
          Performance Metrics
        </div>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-value">{currentIssuer.stats.totalIssuances}</div>
            <div className="stat-label">Total Issuances</div>
            <div className="stat-change positive">+12% this month</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">${(currentIssuer.stats.totalVolume / 1000000).toFixed(1)}M</div>
            <div className="stat-label">Total Volume</div>
            <div className="stat-change positive">+8% this month</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">97.2%</div>
            <div className="stat-label">Success Rate</div>
            <div className="stat-change positive">+0.5% this month</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{currentIssuer.stats.pendingVerifications}</div>
            <div className="stat-label">Pending Verifications</div>
            <div className="stat-change negative">+3 today</div>
          </div>
        </div>
      </StatsSection>

      <RecentActivity>
        <div className="section-title">
          <FiActivity className="icon" />
          Recent Activity
        </div>
        <div className="activity-list">
          <div className="activity-item">
            <div className="activity-icon">
              <FiDollarSign />
            </div>
            <div className="activity-details">
              <div className="activity-title">Minted 500 Gold Tokens</div>
              <div className="activity-description">LBMA Gold Bar - Serial: GB-2024-0156</div>
            </div>
            <div className="activity-time">2 hours ago</div>
          </div>
          <div className="activity-item">
            <div className="activity-icon">
              <FiShield />
            </div>
            <div className="activity-details">
              <div className="activity-title">Asset Verification Completed</div>
              <div className="activity-description">Silver Bar Collection - 12 items verified</div>
            </div>
            <div className="activity-time">5 hours ago</div>
          </div>
          <div className="activity-item">
            <div className="activity-icon">
              <FiTruck />
            </div>
            <div className="activity-details">
              <div className="activity-title">Delivery Request Processed</div>
              <div className="activity-description">Platinum bars to secure vault NYC-001</div>
            </div>
            <div className="activity-time">1 day ago</div>
          </div>
        </div>
      </RecentActivity>

      {/* Render Active Interface */}
      <AnimatePresence>
        {activeInterface && renderInterface()}
      </AnimatePresence>
    </Container>
  );
};

export default IssuerDashboard; 